import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppService } from 'app/app.service';
import { IConfigService } from 'app/config/iconfigservice';
import { FeatureToggle } from 'app/enums';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class FeatureManagerService {
    private apiUrl: string;
    private baseHeaders: HttpHeaders = new HttpHeaders();
    private loading = false;

    public _featureFlags: FeatureToggleResponse[] = null;
    private _tenantFeatureFlags = new Map<string, FeatureToggleResponse[]>();
    private readonly _tenantFeatureFlagRequests = new Map<string, Observable<FeatureToggleResponse[]>>();

    public featureFlags = new BehaviorSubject<FeatureToggleResponse[]>([]);
    public featureFlags$ = this.featureFlags.asObservable();
 
    constructor(private configService: IConfigService,
    private httpClient: HttpClient,
    private appService: AppService)
    {
    }

    loadFeatureFlags() {
        if (this.loading) return;

        const cachedFlags = this.configService.getFeatureFlags();
        if (cachedFlags?.length) {
            this._featureFlags = cachedFlags;
            this.featureFlags.next(cachedFlags);
            this.featureFlags$ = this.featureFlags.asObservable();
            this.loading = true;
            return;
        }

        this.baseHeaders = this.appService.getHeaders();
        const publicApiUrl = this.configService.getConfiguration().publicApiUrl;
        this.apiUrl = `${publicApiUrl}global/featureflags`;

        this.loading = true;
        this.featureFlags$ = this.httpClient.get<FeatureToggleResponse[]>(this.apiUrl, { headers: this.baseHeaders }).pipe(shareReplay(1));
        this.featureFlags$.subscribe({
            next: _result => {
                this._featureFlags = _result;
            },
            error: error => {
                this.appService.showResponseErrorMsg(error);
            }
        });
    }

    getFeatureFlagClients(): Observable<FeatureFlagClientResponse[]> {
        const headers = this.appService.getHeaders();
        const publicApiUrl = this.configService.getConfiguration().publicApiUrl;
        const url = `${publicApiUrl}global/featureflags/clients`;

        return this.httpClient.get<FeatureFlagClientResponse[]>(url, { headers }).pipe(
            catchError(error => {
                this.appService.showResponseErrorMsg(error);
                return of([]);
            })
        );
    }

    getByName(featureName: FeatureToggle | string) {
        const featureFlag = this._featureFlags?.find(x => x.name === featureName);

        return featureFlag || { enabled: false, name: 'undefined' };
    }

    isEnabled(featureName: FeatureToggle | string, tenantId?: string) {
        if (tenantId) {
            const cached = this._tenantFeatureFlags.get(tenantId);
            if (cached) {
                const flag = cached.find(f => f.name === featureName);
                return of(flag?.enabled ?? false);
            }

            let request = this._tenantFeatureFlagRequests.get(tenantId);
            if (!request) {
                const headers = this.appService.getHeaders(tenantId);
                const publicApiUrl = this.configService.getConfiguration().publicApiUrl;
                const url = `${publicApiUrl}global/featureFlagsFiltered/${tenantId}`;
                request = this.httpClient.get<FeatureToggleResponse[]>(url, { headers }).pipe(
                    tap(flags => this._tenantFeatureFlags.set(tenantId, flags)),
                    finalize(() => this._tenantFeatureFlagRequests.delete(tenantId)),
                    shareReplay({ bufferSize: 1, refCount: false })
                );
                this._tenantFeatureFlagRequests.set(tenantId, request);
            }

            return request.pipe(map(flags => flags.find(f => f.name === featureName)?.enabled ?? false));
        }

        if (this._featureFlags) {
            const flag = this._featureFlags.find(f => f.name === featureName);
            return of(flag?.enabled ?? false);
        }

        this.baseHeaders = this.appService.getHeaders();
        const publicApiUrl = this.configService.getConfiguration().publicApiUrl;
        this.apiUrl = `${publicApiUrl}global/FeatureFlags/${featureName}`;

        return this.httpClient.get<boolean>(this.apiUrl, { headers: this.baseHeaders }).pipe(shareReplay(1));
    }
}

export interface FeatureToggleResponse {
    enabled: boolean;
    name?: string;
    description?: string;
    conditions?: FeatureConditions;
}

export interface FeatureFlagClientResponse {
    id: string;
    name: string;
}

export interface FeatureConditions {
    EnabledFor?: FeatureFilterDefinition[];
    enabledFor?: FeatureFilterDefinition[];
}

export interface FeatureFilterDefinition {
    Name?: string;
    name?: string;
    Parameters?: FeatureFilterParameters;
    parameters?: FeatureFilterParameters;
}

export interface FeatureFilterParameters {
    Clients?: FeatureFilterItem[];
    clients?: FeatureFilterItem[];
    Users?: FeatureFilterItem[];
    users?: FeatureFilterItem[];
    ConnectionNames?: unknown[];
    connectionNames?: unknown[];
    [key: string]: unknown;
}

export interface FeatureFilterItem {
    Id?: string;
    id?: string;
    [key: string]: unknown;
}
